import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { onCLS, onLCP, onFCP, onTTFB, onINP } from "web-vitals";
import App from "./App";

// ============================================================
// SENTRY ERROR TRACKING + SESSION REPLAY
// ============================================================
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,

    integrations: [
      // Performance: tracks page loads, navigations, and web vitals
      Sentry.browserTracingIntegration(),

      // Session Replay: records what users see so you can replay errors
      Sentry.replayIntegration({
        // Mask all text/inputs by default (PII safety)
        maskAllText: false, // CRM data is internal, no public PII risk
        blockAllMedia: false,
      }),
    ],

    // Performance: sample 10% of page loads
    tracesSampleRate: 0.1,

    // Session Replay: record 10% of all sessions, 100% of sessions with errors
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Don't send these errors (common noise)
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection",
      /Loading chunk \d+ failed/,
      /AbortError/,
    ],

    // Release tracking — ties errors to specific deployments
    release: `altara@${__BUILD_COMMIT__}`,
  });
  console.log("[Sentry] Error tracking + session replay initialized");
} else if (import.meta.env.PROD) {
  console.warn("[Sentry] No DSN configured - errors will not be tracked");
}

// ============================================================
// WEB VITALS PERFORMANCE MONITORING
// ============================================================
// Logs Core Web Vitals to console in dev, can send to analytics in prod
function reportWebVitals(metric: { name: string; value: number; rating: string }) {
  const { name, value, rating } = metric;

  // Color code by rating
  const color = rating === "good" ? "🟢" : rating === "needs-improvement" ? "🟡" : "🔴";

  if (import.meta.env.DEV) {
    console.log(`${color} [Web Vital] ${name}: ${Math.round(value)}ms (${rating})`);
  }

  // In production, you could send to analytics:
  // if (import.meta.env.PROD && window.gtag) {
  //   window.gtag('event', name, { value: Math.round(value), metric_rating: rating });
  // }
}

// Core Web Vitals
onCLS(reportWebVitals); // Cumulative Layout Shift (visual stability)
onINP(reportWebVitals); // Interaction to Next Paint (interactivity) - replaces FID
onLCP(reportWebVitals); // Largest Contentful Paint (loading)
onFCP(reportWebVitals); // First Contentful Paint (initial render)
onTTFB(reportWebVitals); // Time to First Byte (server response)

// ============================================================
// BUILD INFO (verify deployments)
// ============================================================
declare const __BUILD_COMMIT__: string;
declare const __BUILD_DATE__: string;

// Always log build info - helps verify which version is deployed
console.log(
  `%c🚀 Altara Build: ${__BUILD_COMMIT__} (${new Date(__BUILD_DATE__).toLocaleDateString("en-US")})`,
  "color: #10b981; font-weight: bold"
);

// Expose to window for easy checking in browser console
if (typeof window !== "undefined") {
  (window as unknown as { __ALTARA_BUILD__: { commit: string; date: string } }).__ALTARA_BUILD__ = {
    commit: __BUILD_COMMIT__,
    date: __BUILD_DATE__,
  };
}

// ============================================================
// RENDER APP
// ============================================================
// Short course links: /c/<5 chars> (vercel.json rewrites it to index.html).
// HashRouter only reads the part after "#", so hand the code to the Course page
// before React mounts. COURSE_PAGE handoff, P1 course deep links.
{
  const m = /^\/c\/([a-z0-9]{5})\/?$/i.exec(window.location.pathname);
  if (m) window.history.replaceState(null, "", `/#/admin/course?k=${m[1].toLowerCase()}`);
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
